// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { useCallback, useEffect, useState } from 'react';
import { Checkbox, ConfigProvider, Form, InputNumber, type ThemeConfig } from 'antd';
import { useTranslation } from 'react-i18next';
import { UndoOutlined } from '@ant-design/icons';
import { Button } from '@imprivata-cloud/components';

import { Entitlement } from '@api/constants';
import type { PasswordPolicyUpdateRequest } from '@api/types';
import { hasEntitlement } from '@api/utils';
import { withRole } from '@common/authorization/Roles';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { IDLE_TIMEOUT_LOGOUT_EVENT, ROLE_TYPE_ADMIN } from '../../constants';
import { useUnsavedChangesPrompt } from '../../hooks/useUnsavedChangesPrompt';
import FaceSettings, { useFaceSettings } from '../security/FaceSettings';
import { usePasswordPolicyMutation, usePasswordPolicyQuery } from './usePasswordPolicy';
import { useSession } from '@/common/authorization/context/Session';
import { isPasswordExpirationEnabledForTenant } from '@/featureFlagsUtils';

import './PasswordPolicy.less';

const TRANSLATION_ROOT = 'portal.password-policy';
const DEFAULT_PASSWORD_REUSE_COUNT = 5;

const FORM_THEME: ThemeConfig = {
  components: {
    InputNumber: { controlHeight: 48, colorBorder: '#999999', inputFontSize: 16 },
    Checkbox: {
      controlInteractiveSize: 20,
      borderRadiusSM: 4,
      colorBorder: '#999999',
      colorBgContainer: '#ffffff',
      colorPrimary: '#355787',
      colorPrimaryHover: '#355787',
    },
  },
};

export enum FieldNames {
  MINIMUM_LENGTH = 'minimumLength',
  MAXIMUM_LENGTH = 'maximumLength',
  REQUIRE_ALPHABETIC = 'requireAlphabetic',
  REQUIRE_NUMERIC = 'requireNumeric',
  REQUIRE_SPECIAL = 'requireSpecial',
  REQUIRE_LOWERCASE = 'requireLowercase',
  REQUIRE_UPPERCASE = 'requireUppercase',
  PREVENT_PASSWORD_REUSE = 'preventPasswordReuse',
  PASSWORD_REUSE_COUNT = 'passwordReuseCount',
  PASSWORD_EXPIRATION_ENABLED = 'passwordExpirationEnabled',
  PASSWORD_EXPIRATION_DAYS = 'passwordExpirationDays',
}

export interface FormValues {
  [FieldNames.MINIMUM_LENGTH]?: number;
  [FieldNames.MAXIMUM_LENGTH]?: number;
  [FieldNames.REQUIRE_ALPHABETIC]: boolean;
  [FieldNames.REQUIRE_NUMERIC]: boolean;
  [FieldNames.REQUIRE_SPECIAL]: boolean;
  [FieldNames.REQUIRE_LOWERCASE]: boolean;
  [FieldNames.REQUIRE_UPPERCASE]: boolean;
  [FieldNames.PREVENT_PASSWORD_REUSE]: boolean;
  [FieldNames.PASSWORD_REUSE_COUNT]?: number;
  [FieldNames.PASSWORD_EXPIRATION_ENABLED]: boolean;
  [FieldNames.PASSWORD_EXPIRATION_DAYS]?: number;
}

function PasswordPolicy() {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const { emitError, clearErrorNotifications } = useErrorContext();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const { tenantId, tenantInfo } = useSession();
  const isPasswordExpirationEnabled = isPasswordExpirationEnabledForTenant(tenantId || '');
  const hasFaceRecognition = hasEntitlement(tenantInfo, Entitlement.FACE_RECOGNITION);
  const face = useFaceSettings(hasFaceRecognition);
  const { undo: undoFace } = face;
  const hasUnsavedChanges = isFormDirty || face.dirty;

  // Prompt user when navigating away with unsaved changes
  useUnsavedChangesPrompt({ hasUnsavedChanges });

  const handleNumericKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey || e.key.length > 1 || /[0-9]/.test(e.key)) {
      return;
    }
    e.preventDefault();
  };

  const { data: policyData, isFetching, error: fetchError, refetch } = usePasswordPolicyQuery();

  const { mutate: updatePolicy, isPending: isUpdating } = usePasswordPolicyMutation();

  useEffect(() => {
    if (fetchError) {
      emitError(new AppError(fetchError as unknown as ApiError));
    }
  }, [fetchError, emitError]);

  useEffect(() => {
    if (policyData) {
      const passwordReuseValue = policyData.passwordReuse?.value ?? 0;
      const expirationValue = policyData.passwordExpiration?.value ?? 0;
      const formValues: FormValues = {
        minimumLength: policyData.minimumLength?.value ?? 15,
        maximumLength: policyData.maximumLength?.value,
        requireAlphabetic: policyData.requireAlphabetic?.value ?? false,
        requireNumeric: policyData.requireNumeric?.value ?? false,
        requireSpecial: policyData.requireSpecial?.value ?? false,
        requireLowercase: policyData.requireLowercase?.value ?? false,
        requireUppercase: policyData.requireUppercase?.value ?? false,
        preventPasswordReuse: passwordReuseValue > 0,
        passwordReuseCount: passwordReuseValue > 0 ? passwordReuseValue : undefined,
        passwordExpirationEnabled: expirationValue > 0,
        passwordExpirationDays: expirationValue > 0 ? expirationValue : undefined,
      };
      form.setFieldsValue(formValues);
    }
  }, [policyData, form]);

  const handleUndo = useCallback(async () => {
    clearErrorNotifications();
    undoFace();
    if (policyData) {
      const passwordReuseValue = policyData.passwordReuse?.value ?? 0;
      const expirationValue = policyData.passwordExpiration?.value ?? 0;
      const formValues: FormValues = {
        minimumLength: policyData.minimumLength?.value ?? 15,
        maximumLength: policyData.maximumLength?.value,
        requireAlphabetic: policyData.requireAlphabetic?.value ?? false,
        requireNumeric: policyData.requireNumeric?.value ?? false,
        requireSpecial: policyData.requireSpecial?.value ?? false,
        requireLowercase: policyData.requireLowercase?.value ?? false,
        requireUppercase: policyData.requireUppercase?.value ?? false,
        preventPasswordReuse: passwordReuseValue > 0,
        passwordReuseCount: passwordReuseValue > 0 ? passwordReuseValue : undefined,
        passwordExpirationEnabled: expirationValue > 0,
        passwordExpirationDays: expirationValue > 0 ? expirationValue : undefined,
      };
      form.setFieldsValue(formValues);
      for (const fieldName of Object.values(FieldNames)) {
        form.setFields([{ name: fieldName, errors: [] }]);
      }
    }
    setIsFormDirty(false);
  }, [policyData, form, clearErrorNotifications, undoFace]);

  const isLoading = isFetching || isUpdating;

  const minLengthConstraints = policyData?.minimumLength?.constraints || { min: 8, max: 256 };
  const maxLengthConstraints = policyData?.maximumLength?.constraints || { min: 8, max: 256 };
  const passwordReuseConstraints = policyData?.passwordReuse?.constraints || { min: 0, max: 30 };

  // Track form validation state
  const [hasErrors, setHasErrors] = useState(false);

  // Watch all form values to trigger validation check
  const formValues = Form.useWatch([], form);

  useEffect(() => {
    // Check for validation errors synchronously
    // This checks the current error state without triggering new validation
    // formValues is used as a dependency to trigger this effect when form values change
    if (formValues !== undefined) {
      const fieldErrors = form.getFieldsError();
      const hasValidationErrors = fieldErrors.some(
        field => field.errors && field.errors.length > 0,
      );
      setHasErrors(hasValidationErrors);
    }
  }, [form, formValues]);

  // Enter is left to the browser: in a field it submits through Save, so it does nothing while
  // Save is disabled, and on a button it runs that button.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Escape' && hasUnsavedChanges) {
      event.preventDefault();
      void handleUndo();
    }
  };

  // Listen for idle timeout event to clear unsaved changes state
  // This prevents the browser's beforeunload dialog from blocking logout
  useEffect(() => {
    const handleIdleTimeout = () => {
      console.debug('[PasswordPolicy] Idle timeout detected, clearing form dirty state');
      setIsFormDirty(false);
      undoFace();
    };

    window.addEventListener(IDLE_TIMEOUT_LOGOUT_EVENT, handleIdleTimeout);
    return () => {
      window.removeEventListener(IDLE_TIMEOUT_LOGOUT_EVENT, handleIdleTimeout);
    };
  }, [undoFace]);

  function onFinish(formValues: FormValues) {
    clearErrorNotifications();
    if (face.dirty) {
      face.save();
    }
    if (isFormDirty) {
      savePasswordPolicy(formValues);
    }
  }

  function savePasswordPolicy(formValues: FormValues) {
    let minimumLength = formValues[FieldNames.MINIMUM_LENGTH];
    if (!minimumLength || minimumLength < minLengthConstraints.min) {
      minimumLength = 15;
      form.setFieldValue(FieldNames.MINIMUM_LENGTH, 15);
    }

    let maximumLength = formValues[FieldNames.MAXIMUM_LENGTH];
    if (!maximumLength || maximumLength < maxLengthConstraints.min) {
      maximumLength = maxLengthConstraints.max;
      form.setFieldValue(FieldNames.MAXIMUM_LENGTH, maximumLength);
    }

    const preventPasswordReuse = formValues[FieldNames.PREVENT_PASSWORD_REUSE];
    let passwordReuseValue = 0;

    if (preventPasswordReuse) {
      const reuseCount = formValues[FieldNames.PASSWORD_REUSE_COUNT];

      // If empty, set to default value (validation allows empty, will be auto-fixed here)
      if (reuseCount === undefined || reuseCount === null) {
        const minValue = Math.max(passwordReuseConstraints.min, 1);
        const clampedDefault = Math.min(
          Math.max(DEFAULT_PASSWORD_REUSE_COUNT, minValue),
          passwordReuseConstraints.max,
        );
        passwordReuseValue = clampedDefault;
        form.setFieldValue(FieldNames.PASSWORD_REUSE_COUNT, clampedDefault);
      } else {
        // Validation ensures this is a valid number in range
        passwordReuseValue = reuseCount;
      }
    }

    const expirationCheckboxEnabled = formValues[FieldNames.PASSWORD_EXPIRATION_ENABLED];
    const rawDays = formValues[FieldNames.PASSWORD_EXPIRATION_DAYS];
    const parsedDays = Number(rawDays);
    const expirationDays =
      expirationCheckboxEnabled && Number.isFinite(parsedDays) && parsedDays > 0 ? parsedDays : 0;

    const updateRequest: PasswordPolicyUpdateRequest = {
      minimumLength: minimumLength,
      maximumLength: maximumLength,
      requireAlphabetic: formValues[FieldNames.REQUIRE_ALPHABETIC],
      requireNumeric: formValues[FieldNames.REQUIRE_NUMERIC],
      requireSpecial: formValues[FieldNames.REQUIRE_SPECIAL],
      requireLowercase: formValues[FieldNames.REQUIRE_LOWERCASE],
      requireUppercase: formValues[FieldNames.REQUIRE_UPPERCASE],
      passwordReuse: passwordReuseValue,
      ...(isPasswordExpirationEnabled ? { passwordExpiration: expirationDays } : {}),
    };

    updatePolicy(updateRequest, {
      onSuccess: () => {
        void refetch();
        setIsFormDirty(false);
      },
      onError: (e: ApiError) => {
        emitError(new AppError(e));
      },
    });
  }

  const validateMinimumLength = (_: unknown, value: number | string | undefined) => {
    // Check if value is empty/undefined/null/0 or empty string
    if (value === undefined || value === null || value === 0 || value === '') {
      return Promise.reject(new Error(t(`${TRANSLATION_ROOT}.validation.minimum-length-required`)));
    }

    // Ensure value is a number (type guard)
    if (typeof value !== 'number') {
      return Promise.reject(new Error(t(`${TRANSLATION_ROOT}.validation.minimum-length-required`)));
    }

    const maxLength = form.getFieldValue(FieldNames.MAXIMUM_LENGTH);
    if (maxLength && value > maxLength) {
      return Promise.reject(
        new Error(t(`${TRANSLATION_ROOT}.validation.minimum-greater-than-maximum`)),
      );
    }
    return Promise.resolve();
  };

  const validateMaximumLength = (_: unknown, value: number | string | undefined) => {
    if (!value) {
      return Promise.reject(new Error(t(`${TRANSLATION_ROOT}.validation.maximum-length-required`)));
    }

    // Ensure value is a number (type guard)
    if (typeof value !== 'number') {
      return Promise.reject(new Error(t(`${TRANSLATION_ROOT}.validation.maximum-length-required`)));
    }

    const minLength = form.getFieldValue(FieldNames.MINIMUM_LENGTH);
    if (minLength && value < minLength) {
      return Promise.reject(
        new Error(t(`${TRANSLATION_ROOT}.validation.maximum-less-than-minimum`)),
      );
    }
    return Promise.resolve();
  };

  const validatePasswordReuseCount = (_: unknown, value: number | string | undefined) => {
    const preventPasswordReuse = form.getFieldValue(FieldNames.PREVENT_PASSWORD_REUSE);

    if (!preventPasswordReuse) {
      return Promise.resolve();
    }

    // Allow empty values (will be set to default in onFinish)
    if (value === undefined || value === null || value === '') {
      return Promise.resolve();
    }

    // Ensure value is a number (type guard)
    if (typeof value !== 'number') {
      return Promise.reject(new Error(t(`${TRANSLATION_ROOT}.validation.password-reuse-integer`)));
    }

    // Reject non-integer or non-finite values
    if (!Number.isInteger(value) || !Number.isFinite(value)) {
      return Promise.reject(new Error(t(`${TRANSLATION_ROOT}.validation.password-reuse-integer`)));
    }

    const minValue = Math.max(passwordReuseConstraints.min, 1);
    if (value < minValue || value > passwordReuseConstraints.max) {
      return Promise.reject(
        new Error(
          t(`${TRANSLATION_ROOT}.validation.password-reuse-range`, {
            min: minValue,
            max: passwordReuseConstraints.max,
          }),
        ),
      );
    }

    return Promise.resolve();
  };

  const validatePasswordExpirationDays = (_: unknown, value: number | string | undefined) => {
    const enabled = form.getFieldValue(FieldNames.PASSWORD_EXPIRATION_ENABLED);
    if (!enabled) {
      return Promise.resolve();
    }
    const numericValue = Number(value);
    if (numericValue < 1 || numericValue > 999) {
      return Promise.reject(new Error(t(`${TRANSLATION_ROOT}.fields.expiration.error-days`)));
    }

    return Promise.resolve();
  };

  return (
    <>
      {hasFaceRecognition && <FaceSettings face={face} />}
      <ConfigProvider theme={FORM_THEME}>
        <Form
          id="password-policy-form"
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onValuesChange={() => setIsFormDirty(true)}
          onKeyDown={handleKeyDown}
          className="password-policy-form"
          aria-labelledby="password-policy-header"
        >
          <div className="password-policy-container">
            <div className="form-section">
              <div className="password-policy-heading">
                <h2 id="password-policy-header" className="section-title">
                  {t(`${TRANSLATION_ROOT}.header`)}
                </h2>
                <p>{t(`${TRANSLATION_ROOT}.description`)}</p>
              </div>

              <div className="password-length-row">
                <Form.Item
                  name={FieldNames.MINIMUM_LENGTH}
                  label={t(`${TRANSLATION_ROOT}.fields.minimum-length.label`)}
                  extra={t(`${TRANSLATION_ROOT}.fields.minimum-length.helper`)}
                  rules={[{ validator: validateMinimumLength }]}
                  validateTrigger={['onChange', 'onBlur']}
                >
                  <InputNumber
                    min={minLengthConstraints.min}
                    max={minLengthConstraints.max}
                    disabled={isLoading}
                    placeholder="15"
                    className="password-minimum-length-input"
                    onKeyDown={handleNumericKeyDown}
                  />
                </Form.Item>

                <Form.Item
                  name={FieldNames.MAXIMUM_LENGTH}
                  label={t(`${TRANSLATION_ROOT}.fields.maximum-length.label`)}
                  extra={t(`${TRANSLATION_ROOT}.fields.maximum-length.helper`)}
                  rules={[{ validator: validateMaximumLength }]}
                  validateTrigger={['onChange', 'onBlur']}
                >
                  <InputNumber
                    min={maxLengthConstraints.min}
                    max={maxLengthConstraints.max}
                    disabled={isLoading}
                    placeholder=""
                    className="password-maximum-length-input"
                    onKeyDown={handleNumericKeyDown}
                  />
                </Form.Item>
              </div>

              <div className="password-policy-group">
                <p>{t(`${TRANSLATION_ROOT}.password-complexity-label`)}</p>

                <Form.Item name={FieldNames.REQUIRE_ALPHABETIC} valuePropName="checked">
                  <Checkbox disabled={isLoading}>
                    {t(`${TRANSLATION_ROOT}.fields.require-alphabetic.label`)}
                  </Checkbox>
                </Form.Item>

                <Form.Item name={FieldNames.REQUIRE_UPPERCASE} valuePropName="checked">
                  <Checkbox disabled={isLoading}>
                    {t(`${TRANSLATION_ROOT}.fields.require-uppercase.label`)}
                  </Checkbox>
                </Form.Item>

                <Form.Item name={FieldNames.REQUIRE_LOWERCASE} valuePropName="checked">
                  <Checkbox disabled={isLoading}>
                    {t(`${TRANSLATION_ROOT}.fields.require-lowercase.label`)}
                  </Checkbox>
                </Form.Item>

                <Form.Item name={FieldNames.REQUIRE_NUMERIC} valuePropName="checked">
                  <Checkbox disabled={isLoading}>
                    {t(`${TRANSLATION_ROOT}.fields.require-numeric.label`)}
                  </Checkbox>
                </Form.Item>

                <Form.Item name={FieldNames.REQUIRE_SPECIAL} valuePropName="checked">
                  <Checkbox disabled={isLoading}>
                    {t(`${TRANSLATION_ROOT}.fields.require-special.label`)}
                  </Checkbox>
                </Form.Item>
              </div>

              <hr className="password-policy-divider" />

              <div className="password-policy-group">
                <Form.Item name={FieldNames.PREVENT_PASSWORD_REUSE} valuePropName="checked">
                  <Checkbox
                    disabled={isLoading}
                    onChange={e => {
                      if (e.target.checked) {
                        // When enabling, set default value clamped to constraints
                        const minValue = Math.max(passwordReuseConstraints.min, 1);
                        const clampedDefault = Math.min(
                          Math.max(DEFAULT_PASSWORD_REUSE_COUNT, minValue),
                          passwordReuseConstraints.max,
                        );
                        form.setFieldValue(FieldNames.PASSWORD_REUSE_COUNT, clampedDefault);
                      } else {
                        // When disabling, clear the value
                        form.setFieldValue(FieldNames.PASSWORD_REUSE_COUNT, undefined);
                      }
                    }}
                  >
                    {t(`${TRANSLATION_ROOT}.fields.prevent-password-reuse.label`)}
                  </Checkbox>
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues[FieldNames.PREVENT_PASSWORD_REUSE] !==
                    currentValues[FieldNames.PREVENT_PASSWORD_REUSE]
                  }
                >
                  {({ getFieldValue }) => {
                    const preventPasswordReuse = getFieldValue(FieldNames.PREVENT_PASSWORD_REUSE);
                    return (
                      <div className="password-policy-limit">
                        <span id="password-reuse-prefix">
                          {t(`${TRANSLATION_ROOT}.fields.password-reuse.prefix`)}
                        </span>
                        <div className="password-policy-limit-row">
                          <Form.Item
                            name={FieldNames.PASSWORD_REUSE_COUNT}
                            rules={[{ validator: validatePasswordReuseCount }]}
                            dependencies={[FieldNames.PREVENT_PASSWORD_REUSE]}
                            extra={t(`${TRANSLATION_ROOT}.fields.range`, {
                              min: Math.max(passwordReuseConstraints.min, 1),
                              max: passwordReuseConstraints.max,
                            })}
                          >
                            <InputNumber
                              min={Math.max(passwordReuseConstraints.min, 1)}
                              max={passwordReuseConstraints.max}
                              step={1}
                              disabled={isLoading || !preventPasswordReuse}
                              aria-labelledby="password-reuse-prefix password-reuse-suffix"
                              className="password-policy-limit-input"
                              onKeyDown={handleNumericKeyDown}
                            />
                          </Form.Item>
                          <span id="password-reuse-suffix" className="password-policy-limit-suffix">
                            {t(`${TRANSLATION_ROOT}.fields.password-reuse.suffix`)}
                          </span>
                        </div>
                      </div>
                    );
                  }}
                </Form.Item>
              </div>

              {isPasswordExpirationEnabled && (
                <div className="password-policy-group">
                  <Form.Item name={FieldNames.PASSWORD_EXPIRATION_ENABLED} valuePropName="checked">
                    <Checkbox
                      disabled={isLoading}
                      onChange={e => {
                        if (e.target.checked) {
                          const currentValue = form.getFieldValue(
                            FieldNames.PASSWORD_EXPIRATION_DAYS,
                          );
                          if (!currentValue || Number(currentValue) <= 0) {
                            form.setFieldValue(FieldNames.PASSWORD_EXPIRATION_DAYS, 365);
                          }
                        } else {
                          form.setFieldValue(FieldNames.PASSWORD_EXPIRATION_DAYS, undefined);
                        }
                      }}
                    >
                      {t(`${TRANSLATION_ROOT}.fields.expiration.checkbox`)}
                    </Checkbox>
                  </Form.Item>

                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues[FieldNames.PASSWORD_EXPIRATION_ENABLED] !==
                      currentValues[FieldNames.PASSWORD_EXPIRATION_ENABLED]
                    }
                  >
                    {({ getFieldValue }) => {
                      const enabled = getFieldValue(FieldNames.PASSWORD_EXPIRATION_ENABLED);
                      return (
                        <div className="password-policy-limit">
                          <span id="password-expiration-maximum">
                            {t(`${TRANSLATION_ROOT}.fields.expiration.maximum`)}
                          </span>
                          <div className="password-policy-limit-row">
                            <Form.Item
                              name={FieldNames.PASSWORD_EXPIRATION_DAYS}
                              rules={[{ validator: validatePasswordExpirationDays }]}
                              dependencies={[FieldNames.PASSWORD_EXPIRATION_ENABLED]}
                              validateTrigger={['onChange', 'onBlur']}
                              extra={t(`${TRANSLATION_ROOT}.fields.range`, { min: 1, max: 999 })}
                            >
                              <InputNumber
                                disabled={isLoading || !enabled}
                                aria-labelledby="password-expiration-maximum password-expiration-days"
                                className="password-policy-limit-input"
                                onKeyDown={handleNumericKeyDown}
                              />
                            </Form.Item>
                            <span
                              id="password-expiration-days"
                              className="password-policy-limit-suffix"
                            >
                              {t(`${TRANSLATION_ROOT}.fields.expiration.days`)}
                            </span>
                          </div>
                        </div>
                      );
                    }}
                  </Form.Item>
                </div>
              )}
            </div>
          </div>

          <div className="password-policy-container">
            <div className="buttons-section">
              <Button
                type="text"
                className="text-button"
                onClick={handleUndo}
                disabled={!hasUnsavedChanges}
                icon={<UndoOutlined className="undo-icon" />}
              >
                {t(`${TRANSLATION_ROOT}.buttons.undo`)}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                disabled={isLoading || face.saving || !hasUnsavedChanges || hasErrors}
              >
                {t(`${TRANSLATION_ROOT}.buttons.save`)}
              </Button>
            </div>
          </div>
        </Form>
      </ConfigProvider>
    </>
  );
}

export default withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(PasswordPolicy);
